// Ctrl/Cmd+K command palette: jump to any page or game (and admin sections for admins). Mounted once in main.tsx.
// Anything can open it with `openPalette()`.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import { useApi } from "./api.ts";
import { useAuth } from "./auth.tsx";
import { cn, Icon, type IconName } from "./ui/index.tsx";
import { DISCORD_INVITE } from "./ui/logo.tsx";
import type { Game } from "../shared/types.ts";

const OPEN_EVENT = "exo:palette";
export const openPalette = () => window.dispatchEvent(new Event(OPEN_EVENT));

interface Item { id: string; label: string; group: string; icon: IconName; hint?: string; keywords?: string; to?: string; href?: string }

const PUBLIC: Item[] = [
  { id: "p-home", label: "Home", group: "Pages", icon: "home", to: "/" },
  { id: "p-games", label: "Games", group: "Pages", icon: "gamepad", to: "/games" },
  { id: "p-pricing", label: "Pricing", group: "Pages", icon: "card", to: "/pricing", keywords: "buy price cost plans" },
  { id: "p-status", label: "Status", group: "Pages", icon: "activity", to: "/status", keywords: "uptime detected outage" },
  { id: "p-changelog", label: "Changelog", group: "Pages", icon: "scroll", to: "/changelog", keywords: "updates releases version" },
  { id: "p-discord", label: "Join the Discord", group: "Pages", icon: "users", href: DISCORD_INVITE, keywords: "community server chat" },
];
const MEMBER: Item[] = [
  { id: "m-home", label: "Dashboard", group: "Your account", icon: "home", to: "/home" },
  { id: "m-licenses", label: "Licenses", group: "Your account", icon: "key", to: "/licenses", keywords: "redeem key extend renew" },
  { id: "m-downloads", label: "Downloads", group: "Your account", icon: "download", to: "/downloads", keywords: "loader install" },
  { id: "m-guides", label: "Guides", group: "Your account", icon: "book", to: "/guides", keywords: "help setup error" },
  { id: "m-support", label: "Support", group: "Your account", icon: "lifebuoy", to: "/support", keywords: "ticket help" },
  { id: "m-account", label: "Account", group: "Your account", icon: "user", to: "/account", keywords: "password hwid reset discord link" },
];
const ADMIN: Item[] = [
  ["", "Overview", "chart"], ["users", "Users", "users"], ["keys", "License keys", "key"], ["tickets", "Tickets", "ticket"],
  ["content", "Content", "megaphone"], ["releases", "Releases", "package"], ["games", "Games", "gamepad"], ["incidents", "Status & incidents", "activity"],
  ["payments", "Payments", "card"], ["diagnostics", "Diagnostics", "bug"], ["audit", "Audit log", "scroll"], ["settings", "Settings", "settings"],
].map(([path, label, icon]) => ({ id: `a-${path || "overview"}`, label: label!, group: "Admin", icon: icon as IconName, to: `/admin${path ? `/${path}` : ""}`, keywords: "admin" }));

/** Prefix matches first, then word starts, then anywhere. -1 = no match. */
function score(item: Item, q: string): number {
  if (!q) return 0;
  const label = item.label.toLowerCase();
  if (label.startsWith(q)) return 3;
  if (label.split(/[\s:]+/).some((w) => w.startsWith(q))) return 2;
  if (`${label} ${item.keywords ?? ""} ${item.group.toLowerCase()}`.includes(q)) return 1;
  return -1;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const { me, isAdmin } = useAuth();
  // Only fetch games once someone actually opens it.
  const games = useApi<Game[]>(open ? "/games" : null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener(OPEN_EVENT, onOpen); };
  }, []);
  useEffect(() => { if (open) { setQ(""); setActive(0); } }, [open]);

  const items = useMemo(() => {
    const gameItems: Item[] = (games.data ?? []).filter((g) => g.status !== "discontinued").map((g) => ({
      id: `g-${g.id}`, label: g.name, group: "Games", icon: "gamepad", to: `/games/${g.id}`,
      hint: g.purchasable ? "Available" : "Coming soon", keywords: `${g.category} ${g.anti_cheats.join(" ")} ${g.injection}`,
    }));
    const all = [...PUBLIC, ...gameItems, ...(me ? MEMBER : []), ...(isAdmin ? ADMIN : [])];
    const query = q.trim().toLowerCase();
    return all.map((it, i) => ({ it, s: score(it, query), i })).filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s || a.i - b.i).map((x) => x.it);
  }, [games.data, me, isAdmin, q]);

  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => { list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" }); }, [active]);

  const go = (it: Item | undefined) => {
    if (!it) return;
    setOpen(false);
    if (it.href) window.open(it.href, "_blank", "noopener,noreferrer");
    else if (it.to) navigate(it.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); go(items[active]); }
  };

  let lastGroup = "";
  return (
    <AnimatePresence>
      {open && (
        <motion.div key="palette" className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[14vh]" onKeyDown={onKeyDown}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
          <div className="absolute inset-0 bg-bg/70 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden />
          <motion.div role="dialog" aria-modal="true" aria-label="Command palette"
            className="pane hud hud-accent relative w-full max-w-xl overflow-hidden bg-surface/95 shadow-[0_30px_80px_-20px_rgb(0_0_0/0.9)] backdrop-blur-xl"
            initial={{ opacity: 0, y: -12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 520, damping: 36 }} onAnimationStart={() => input.current?.focus()}>
            <div className="flex items-center gap-3 border-b border-line px-4">
              <Icon name="search" className="size-4 text-faint" />
              <input ref={input} autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Jump to a page or game…"
                role="combobox" aria-expanded="true" aria-controls="palette-list" aria-activedescendant={items[active] ? `pal-${items[active]!.id}` : undefined}
                className="h-14 flex-1 bg-transparent text-[15px] text-fg placeholder:text-faint focus:outline-none" />
              <kbd className="rounded border border-line bg-raised px-1.5 font-mono text-[10px] text-faint">Esc</kbd>
            </div>
            <ul id="palette-list" ref={list} role="listbox" className="max-h-[52vh] overflow-y-auto p-2">
              {items.length === 0 && <li className="px-3 py-8 text-center text-sm text-muted">Nothing matches "{q}".</li>}
              {items.map((it, i) => {
                const header = it.group !== lastGroup ? (lastGroup = it.group) : null;
                return (
                  <li key={it.id} role="presentation">
                    {header && <div className="kicker px-3 pb-1 pt-3 text-[10px] first:pt-1">{header}</div>}
                    <button id={`pal-${it.id}`} type="button" role="option" aria-selected={i === active} data-index={i}
                      onMouseMove={() => setActive(i)} onClick={() => go(it)}
                      className={cn("relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm", i === active ? "text-fg" : "text-muted")}>
                      {i === active && <motion.span layoutId="palette-active" className="absolute inset-0 rounded-lg bg-accent-soft ring-1 ring-accent/30" transition={{ type: "spring", stiffness: 600, damping: 40 }} />}
                      <Icon name={it.icon} className={cn("relative size-4 shrink-0", i === active ? "text-accent" : "text-faint")} />
                      <span className="relative flex-1 truncate">{it.label}</span>
                      {it.hint && <span className="relative font-mono text-[10px] uppercase tracking-wider text-faint">{it.hint}</span>}
                      {it.href && <Icon name="external" className="relative size-3.5 text-faint" />}
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center gap-4 border-t border-line px-4 py-2 font-mono text-[10px] text-faint">
              <span><kbd className="text-muted">↑↓</kbd> move</span><span><kbd className="text-muted">Enter</kbd> open</span><span className="ml-auto"><kbd className="text-muted">Ctrl K</kbd> toggle</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
